import {
  ClientStatus, ClientSummary, InvitationKind, Ledger, PracticeMembership
} from 'src/app/data/service/practice.service';

/**
 * SD-1248: how the All clients page reads a ClientSummary. Pure functions, so the rules that
 * decide what an advisor is told -- what needs action, where each action goes, what a null figure
 * means -- are tested without a component.
 */

export type Tone = 'good' | 'warn' | 'bad' | 'info' | 'neutral';

export type ClientFilter = 'all' | 'attention' | 'working' | 'ok' | 'setting_up' | 'docs' | 'reconcile' | 'overdue';

export const STATUS_VIEW: { [status in ClientStatus]: { label: string; tone: Tone } } = {
  connection_lost: { label: 'Connection lost', tone: 'bad' },
  action_required: { label: 'Action required', tone: 'warn' },
  agent_working: { label: 'Agent working', tone: 'info' },
  up_to_date: { label: 'Up to date', tone: 'good' },
  setting_up: { label: 'Setting up', tone: 'neutral' }
};

export const FILTERS: { id: ClientFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'attention', label: 'Needs action' },
  { id: 'working', label: 'Agent working' },
  { id: 'ok', label: 'Up to date' },
  { id: 'setting_up', label: 'Setting up' }
];

/** "Needs action" is both kinds of trouble: something to do, and a connection to mend. */
export function needsAction(client: ClientSummary): boolean {
  return client.status === 'action_required' || client.status === 'connection_lost';
}

export function matchesFilter(client: ClientSummary, filter: ClientFilter): boolean {
  switch (filter) {
    case 'attention': return needsAction(client);
    case 'working': return client.status === 'agent_working';
    case 'ok': return client.status === 'up_to_date';
    case 'setting_up': return client.status === 'setting_up';
    // The summary cards: the clients with something in that queue.
    case 'docs': return docsForReview(client) > 0;
    case 'reconcile': return (client.transactionsToReconcile || 0) > 0;
    case 'overdue': return (client.billsOverdueAmount || 0) > 0;
    default: return true;
  }
}

/** Documents in the Inbox's For Review; null when the user may see neither half. */
export function docsForReview(client: ClientSummary): number | null {
  if (!client) {
    return null;
  }
  if (client.docsForReview !== undefined && client.docsForReview !== null) {
    return client.docsForReview;
  }
  if (client.expensesDetailsNeeded === null && client.expensesForReview === null) {
    return null;
  }
  return (client.expensesDetailsNeeded || 0) + (client.expensesForReview || 0);
}

// ── Sorting the list ─────────────────────────────────────────────────────────────────────────

export type ClientSort = 'status' | 'name' | 'docs' | 'reconcile' | 'overdue';

const STATUS_ORDER: ClientStatus[] = ['connection_lost', 'action_required', 'setting_up', 'agent_working', 'up_to_date'];

/**
 * The list in the order asked for. 'status' is the server's order (most urgent first) and keeps it;
 * a figure sorts largest first, with unknown figures last; 'name' is A to Z.
 */
export function sortClients(clients: ClientSummary[], sort: ClientSort): ClientSummary[] {
  const list = (clients || []).slice();
  const figure = (pick: (c: ClientSummary) => number | null | undefined) =>
    (a: ClientSummary, b: ClientSummary) => {
      const x = pick(a), y = pick(b);
      const xv = x === null || x === undefined ? -1 : x;
      const yv = y === null || y === undefined ? -1 : y;
      return yv - xv || (a.name || '').localeCompare(b.name || '');
    };
  switch (sort) {
    case 'name': return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    case 'docs': return list.sort(figure(docsForReview));
    case 'reconcile': return list.sort(figure(c => c.transactionsToReconcile));
    case 'overdue': return list.sort(figure(c => c.billsOverdueAmount));
    default:
      // Stable: within a status, the server's order.
      return list.map((c, i) => ({ c, i }))
        .sort((a, b) => (STATUS_ORDER.indexOf(a.c.status) - STATUS_ORDER.indexOf(b.c.status)) || a.i - b.i)
        .map(x => x.c);
  }
}

export function matchesSearch(client: ClientSummary, query: string): boolean {
  const q = (query || '').trim().toLowerCase();
  return !q || (client.name || '').toLowerCase().indexOf(q) >= 0;
}

export function ledgerLabel(ledger: Ledger | string | null | undefined): string {
  switch (ledger) {
    case 'xero': return 'Xero';
    case 'qbo': return 'QuickBooks';
    default: return 'No ledger';
  }
}

/** The ledger's mark, for beside its name; null with no ledger. */
export function ledgerLogo(ledger: Ledger | string | null | undefined): string | null {
  switch (ledger) {
    case 'xero': return 'assets/img/Xero.png';
    case 'qbo': return 'assets/img/company/QBOLogo.png';
    default: return null;
  }
}

/** "6 min ago", "3 hr ago", "2 days ago"; null for no time or one that does not parse. */
function ago(utc: string | null | undefined, now: number): string | null {
  const at = utc ? Date.parse(utc) : NaN;
  if (isNaN(at)) { return null; }
  const minutes = Math.max(0, Math.round((now - at) / 60000));
  if (minutes < 1) { return 'just now'; }
  if (minutes < 60) { return `${minutes} min ago`; }
  const hours = Math.round(minutes / 60);
  if (hours < 24) { return `${hours} hr ago`; }
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

/** "synced 6 min ago", "synced 3 days ago", "never synced". */
export function syncLabel(lastSyncUtc: string | null | undefined, now: number = Date.now()): string {
  const when = ago(lastSyncUtc, now);
  return when ? `synced ${when}` : 'never synced';
}

export interface SyncView {
  text: string;
  /** The latest refresh failed. */
  failed: boolean;
}

/**
 * The client's ledger sync as of its latest refresh, as Settings > Integrations lists them:
 * "synced 2 hr ago" when it worked, "Contacts sync failed 2 hr ago" when it did not, and
 * Nothing when no refresh was ever logged.
 */
export function syncView(client: { lastSyncUtc?: string | null; lastSyncAttemptUtc?: string | null; lastSyncFailedTable?: string | null },
                         now: number = Date.now()): SyncView {
  const attempt = ago(client && client.lastSyncAttemptUtc, now);
  if (attempt && client.lastSyncFailedTable) {
    return { text: `${client.lastSyncFailedTable} sync failed ${attempt}`, failed: true };
  }
  // A client that never synced shows no sync text for now (SD-1252); syncLabel still says "never synced".
  const last = (client && client.lastSyncUtc) || (client && client.lastSyncAttemptUtc);
  return { text: last ? syncLabel(last, now) : '', failed: false };
}

/** A short name for buttons: "Harbour Joinery Pty Ltd" opens as "Open Harbour Joinery". */
export function shortName(name: string): string {
  const trimmed = (name || '').replace(/\s+(Pty\.?\s+Ltd\.?|Pty|Ltd\.?|Limited|LLC|Inc\.?)$/i, '').trim();
  return trimmed || name || '';
}

// ── Totals across clients (the summary cards) ────────────────────────────────────────────────

export interface Total {
  /** Sum over the clients where the figure is known; null when no client reports it. */
  value: number | null;
  /** How many clients have a non-zero figure. */
  clients: number;
}

export function total(clients: ClientSummary[], pick: (c: ClientSummary) => number | null | undefined): Total {
  let value: number | null = null;
  let count = 0;
  (clients || []).forEach(c => {
    const v = pick(c);
    if (v === null || v === undefined) {
      return;
    }
    value = (value || 0) + v;
    if (v > 0) {
      count++;
    }
  });
  return { value, clients: count };
}

// ── The selected client's Needs action list ──────────────────────────────────────────────────

/** Where an action goes, once the advisor has been switched into the client. */
export interface ActionDestination {
  commands: string[];
  queryParams?: { [key: string]: any };
  /** Clear the payable screens' sessionStorage first, as the sidebar's Bills To Pay link does. */
  resetPayable?: boolean;
}

export interface ClientAction {
  key: string;
  code: string;
  title: string;
  badge: string;
  tone: Tone;
  where: string;
  value: number | null;
  /** True when `value` is money rather than a count. */
  money?: boolean;
  destination: ActionDestination;
}

export const DESTINATIONS: { [key: string]: ActionDestination } = {
  expenses: { commands: ['/inbox'], queryParams: { isProcessed: false } },
  statements: { commands: ['/paperwork/missing-paperwork'] },
  bills: { commands: ['/payable/outstanding-amounts'], resetPayable: true },
  transactions: { commands: ['/transactions'] },
  integration: { commands: ['/setting/integration'] },
  bankFeeds: { commands: ['/setting/lists/Banking'] },
  home: { commands: ['/dashboard'] }
};

/**
 * The client's Needs action list, most urgent first: broken connections, then setting up, then
 * money owed, then the work queues. A figure the user may not see (null) is never an action.
 */
export function clientActions(client: ClientSummary): ClientAction[] {
  if (!client) {
    return [];
  }
  const actions: ClientAction[] = [];

  (client.connections || []).filter(c => !c.ok).forEach((connection, i) => {
    const isBankFeed = connection.kind === 'bankfeed';
    actions.push({
      key: 'connection-' + i,
      code: isBankFeed ? 'BF' : 'AC',
      title: isBankFeed ? 'Reconnect bank feed' : 'Reconnect ' + (connection.name || 'accounting'),
      badge: 'Connection lost',
      tone: 'bad',
      where: [connection.detail || connection.name, isBankFeed ? 'Settings › Banking' : 'Settings › Integrations']
        .filter(x => !!x).join(' · '),
      value: null,
      destination: isBankFeed ? DESTINATIONS.bankFeeds : DESTINATIONS.integration
    });
  });

  if (client.status === 'setting_up') {
    actions.push({
      key: 'setting-up',
      code: 'AC',
      title: 'Connect accounting',
      badge: 'Setting up',
      tone: 'neutral',
      where: 'Settings › Integrations',
      value: null,
      destination: DESTINATIONS.integration
    });
  }

  if (client.billsOverdueAmount) {
    actions.push({
      key: 'bills',
      code: 'BP',
      title: 'Overdue bills',
      badge: 'Overdue',
      tone: 'bad',
      where: 'Payables › Bills To Pay',
      value: client.billsOverdueAmount,
      money: true,
      destination: DESTINATIONS.bills
    });
  }

  const docs = docsForReview(client);
  if (docs) {
    const parts = [
      client.expensesDetailsNeeded ? `${client.expensesDetailsNeeded} need details` : '',
      client.expensesForReview ? `${client.expensesForReview} ready to publish` : ''
    ].filter(x => !!x).join(' · ');
    actions.push({
      key: 'expenses',
      code: 'DR',
      title: 'Docs for review',
      badge: client.expensesDetailsNeeded ? 'Details needed' : 'To review',
      tone: 'warn',
      where: 'Inbox › For Review' + (parts ? ' · ' + parts : ''),
      value: docs,
      destination: DESTINATIONS.expenses
    });
  }

  if (client.transactionsToReconcile) {
    actions.push({
      key: 'transactions',
      code: 'TX',
      title: 'Unreconciled transactions',
      badge: 'To reconcile',
      tone: 'info',
      where: 'Banking › Transactions',
      value: client.transactionsToReconcile,
      destination: DESTINATIONS.transactions
    });
  }

  if (client.statementsActionRequired) {
    const awaiting = client.statementsAwaitingApproval ? ` · ${client.statementsAwaitingApproval} awaiting approval` : '';
    actions.push({
      key: 'statements',
      code: 'ST',
      title: 'Statements',
      badge: 'Action required',
      tone: 'warn',
      where: 'Banking › Expense Reports' + awaiting,
      value: client.statementsActionRequired,
      destination: DESTINATIONS.statements
    });
  }

  return actions;
}

// ── Which practice the page shows ────────────────────────────────────────────────────────────

const PRACTICE_KEY = 'SelectedPracticeId';

/**
 * The practice to show: the one asked for, else the one last shown in this browser, else the
 * first. Only ever one of the user's own practices -- an id from the address or storage that is
 * not among them is ignored.
 */
export function pickPractice(practices: PracticeMembership[], requested?: string | null): PracticeMembership | null {
  if (!practices || practices.length === 0) {
    return null;
  }
  const byId = (id: string | null | undefined) =>
    id ? practices.find(p => p.organisationId.toLowerCase() === id.toLowerCase()) : undefined;
  let remembered: string | null = null;
  try { remembered = localStorage.getItem(PRACTICE_KEY); } catch { }
  return byId(requested) || byId(remembered) || practices[0];
}

export function rememberPractice(practiceId: string): void {
  try { localStorage.setItem(PRACTICE_KEY, practiceId); } catch { }
}

export const INVITATION_KIND_LABEL: { [kind in InvitationKind]: string } = {
  client: 'Client',
  member: 'Team member',
  owner: 'Client owner'
};

/** Initials and a colour slot for an organisation without a logo. */
export function avatarSlot(id: string): number {
  let hash = 0;
  for (let i = 0; i < (id || '').length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 6) + 1;
}

// ── A client's logo and details ──────────────────────────────────────────────────────────────

export const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
export const MAX_LOGO_BYTES = 2 * 1024 * 1024;

/** Why a chosen file cannot be a logo, or null when it can. The server checks the bytes again. */
export function logoProblem(file: { type: string; size: number } | null | undefined): string | null {
  if (!file) {
    return 'Choose an image.';
  }
  if (LOGO_TYPES.indexOf((file.type || '').toLowerCase()) < 0) {
    return 'Choose a PNG, JPEG, GIF or WebP image.';
  }
  if (file.size > MAX_LOGO_BYTES) {
    return 'Choose an image under 2 MB.';
  }
  return null;
}

/** "https://www.harbourjoinery.com.au/about" reads "harbourjoinery.com.au/about". */
export function websiteLabel(website: string | null | undefined): string {
  return (website || '').trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/+$/, '');
}

/** "Construction · Australia", from whichever of the two the client has. */
export function aboutLine(client: { industry?: string | null; location?: string | null } | null): string {
  return client ? [client.industry, client.location].filter(x => !!x && !!x.trim()).join(' · ') : '';
}
